import { AlertTriangle } from "@tamagui/lucide-icons";
import {
  Toast as TamaguiToast,
  useToastController,
  useToastState,
} from "@tamagui/toast";
import { useEffect } from "react";
import { styled } from "tamagui";

import { Text } from "@/components/Typography";
import { useLiveRegionStore } from "@/store/liveRegion";
import { getShadow } from "@/theme/helpers";

const StyledToast = styled(TamaguiToast, {
  unstyled: true,
  borderRadius: "$150",
  padding: "$200",
  backgroundColor: "$fillNeutral",
  cursor: "pointer",
  pressStyle: { opacity: 0.8 },
  width: "95vw",
  maxWidth: 500,
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "center",
  gap: "$100",
});

export default function Toast() {
  const currentToast = useToastState();
  const { hide } = useToastController();
  const { announcePolite, announceAssertive } = useLiveRegionStore();

  const toastId = currentToast?.id;
  const toastTitle = currentToast?.title;
  const isError = currentToast?.error ?? true;

  // biome-ignore lint/correctness/useExhaustiveDependencies: toastId triggers re-announcement for new toasts with the same title
  useEffect(() => {
    if (!toastTitle) return;
    if (isError) {
      announceAssertive(toastTitle);
    } else {
      announcePolite(toastTitle);
    }
  }, [toastId, toastTitle, isError, announcePolite, announceAssertive]);

  if (!currentToast) {
    return null;
  }

  return (
    <StyledToast
      key={currentToast.id}
      duration={currentToast.duration}
      viewportName={currentToast.viewportName}
      onPress={hide}
      role={isError ? "alert" : "status"}
      {...getShadow({ blur: "$400" })}
    >
      {isError && (
        <AlertTriangle color="$textPrimary" size="$200" flexShrink={0} />
      )}
      <Text color="$textPrimary" size="$xs" semibold>
        {currentToast.title}
      </Text>
    </StyledToast>
  );
}
